// src/pages/CityDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Star, Clock, Coffee, Award, Utensils } from 'lucide-react';
import '../styles/cityDetail.css';

const CityDetail = () => {
  const { cityName } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [cityData, setCityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCuisine, setSelectedCuisine] = useState('All');
  const [selectedPrice, setSelectedPrice] = useState('All');
  const [selectedRating, setSelectedRating] = useState('All');

  useEffect(() => {
    // In a real app, you would fetch data from an API
    // Simulating API fetch with setTimeout
    setIsLoading(true);
    setTimeout(() => {
        // Only fetch new data if cityData is null or if we're looking at a different city
        if (!cityData || cityData.name.toLowerCase() !== cityName.toLowerCase()) {
          const city = getCityData(cityName);
          setCityData(city);
        }
        setIsLoading(false);
      }, 800);
    }, [cityName, cityData]);

  // This would normally be fetched from an API
  const getCityData = (name) => {
    // const formattedName = name.charAt(0).toUpperCase() + name.slice(1);
    
    // Sample city data
    const cityData = {
      "paris": {
        "name": "Paris",
        "country": "France",
        "headline": "The City of Light",
        "description": "Paris, France's capital, is a major European city and a global center for art, fashion, gastronomy and culture. Its 19th-century cityscape is crisscrossed by wide boulevards and the River Seine. Beyond such landmarks as the Eiffel Tower and the 12th-century, Gothic Notre-Dame cathedral, the city is known for its cafe culture and designer boutiques along the Rue du Faubourg Saint-Honoré.",
        "image": "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?q=80&w=2070",
        "heroImage": "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=2073",
        "restaurants": 347,
        "rating": 4.7,
        "tags": ["Romantic", "Historic", "Foodie Paradise"],
        "popularCuisines": ["French", "Mediterranean", "Middle Eastern", "Japanese", "Italian"],
        "bestTimeToVisit": "April to June, September to October",
        "neighborhoods": [
          {
            "name": "Le Marais",
            "description": "Historic district with medieval architecture, trendy shops and diverse dining options.",
            "image": "https://images.unsplash.com/photo-1551634979-2b13ed7a9255?q=80&w=2070"
          },
          {
            "name": "Montmartre",
            "description": "Bohemian hilltop area with Sacré-Cœur Basilica and vibrant food scene.",
            "image": "https://images.unsplash.com/photo-1548707309-dcebeab9ea9b?q=80&w=2071"
          },
          {
            "name": "Saint-Germain-des-Prés",
            "description": "Intellectual hub with legendary cafés and fine dining establishments.",
            "image": "https://images.unsplash.com/photo-1574314577052-a4ac7da52bfb?q=80&w=1974"
          }
        ]
      },
      "amsterdam": {
        "name": "Amsterdam",
        "country": "Netherlands",
        "headline": "Venice of the North",
        "description": "Amsterdam, the capital of the Netherlands, is known for its artistic heritage, elaborate canal system, and narrow houses with gabled facades, legacies of the city's 17th-century Golden Age.",
        "image": "https://images.unsplash.com/photo-1526689057872-9e6c34a36e3c?q=80&w=2070",
        "heroImage": "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=2070",
        "restaurants": 250,
        "rating": 4.6,
        "tags": ["Canals", "Bicycles", "Artistic"],
        "popularCuisines": ["Dutch", "Indonesian", "French", "International"],
        "bestTimeToVisit": "April to May, September to November",
        "neighborhoods": [] // Added empty array
      },
      "barcelona": {
        "name": "Barcelona",
        "country": "Spain",
        "headline": "The Vibrant Mediterranean Jewel",
        "description": "Barcelona, the cosmopolitan capital of Spain's Catalonia region, is known for its art and architecture. The fantastical Sagrada Família church and other modernist landmarks designed by Antoni Gaudí dot the city.",
        "image": "https://images.unsplash.com/photo-1570125909235-672979c51651?q=80&w=2070",
        "heroImage": "https://images.unsplash.com/photo-1518005020951-eccb494ad742?q=80&w=2070",
        "restaurants": 280,
        "rating": 4.7,
        "tags": ["Beaches", "Architecture", "Nightlife"],
        "popularCuisines": ["Spanish", "Catalan", "Seafood", "Tapas"],
        "bestTimeToVisit": "May to June, September to October",
        "neighborhoods": [] // Added empty array
      },
      "lisbon": {
        "name": "Lisbon",
        "country": "Portugal",
        "headline": "The City of Seven Hills",
        "description": "Lisbon, Portugal's hilly, coastal capital, is known for its cafe culture, historic trams, and stunning views from its many miradouros.",
        "image": "https://images.unsplash.com/photo-1530543787849-128d94430c6b?q=80&w=2070",
        "heroImage": "https://images.unsplash.com/photo-1532366867686-885b3a37b74d?q=80&w=2070",
        "restaurants": 210,
        "rating": 4.5,
        "tags": ["Scenic Views", "Historic", "Foodie Scene"],
        "popularCuisines": ["Portuguese", "Seafood", "Mediterranean", "Pastries"],
        "bestTimeToVisit": "March to May, September to October",
        "neighborhoods": [] // Added empty array
      },
      "madrid": {
        "name": "Madrid",
        "country": "Spain",
        "headline": "The Heart of Spain",
        "description": "Madrid, Spain's central capital, is a city of elegant boulevards and expansive, manicured parks such as the Buen Retiro. It's renowned for its rich repositories of European art, including the Prado Museum.",
        "image": "https://images.unsplash.com/photo-1578674397285-16a5409ff3f4?q=80&w=2070",
        "heroImage": "https://images.unsplash.com/photo-1582560470772-2e1336b3050b?q=80&w=2070",
        "restaurants": 265,
        "rating": 4.6,
        "tags": ["Culture", "Art Museums", "Lively"],
        "popularCuisines": ["Spanish", "Tapas", "Paella", "International"],
        "bestTimeToVisit": "April to June, September to November",
        "neighborhoods": [] // Added empty array
      },
      "milan": {
        "name": "Milan",
        "country": "Italy",
        "headline": "Fashion Capital of the World",
        "description": "Milan, a metropolis in Italy's northern Lombardy region, is a global capital of fashion and design. It's home to the national stock exchange and known for its high-end restaurants and shops.",
        "image": "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=2070",
        "heroImage": "https://images.unsplash.com/photo-1519638399535-1b036603ac77?q=80&w=2070",
        "restaurants": 290,
        "rating": 4.7,
        "tags": ["Luxury", "Fashion", "Historic"],
        "popularCuisines": ["Italian", "Pizza", "Pasta", "Seafood"],
        "bestTimeToVisit": "April to June, September to October",
        "neighborhoods": [] // Added empty array
      },
      "istanbul": {
    "name": "Istanbul",
    "country": "Turkey",
    "headline": "The City Where East Meets West",
    "description": "Istanbul, straddling Europe and Asia across the Bosphorus Strait, is a city steeped in history with iconic landmarks like the Hagia Sophia, Blue Mosque, and Grand Bazaar.",
    "image": "https://images.unsplash.com/photo-1586363104864-5c0b8e6b62dd?q=80&w=2070",
    "heroImage": "https://images.unsplash.com/photo-1569698224621-d3887b6e0c94?q=80&w=2070",
    "restaurants": 400,
    "rating": 4.7,
    "tags": ["Historic", "Cultural Crossroads", "Vibrant Markets"],
    "popularCuisines": ["Turkish", "Mediterranean", "Kebabs", "Baklava"],
    "bestTimeToVisit": "April to June, September to November",
    "neighborhoods": []
  },
      "rome": {
    "name": "Rome",
    "country": "Italy",
    "headline": "The Eternal City",
    "description": "Rome, the capital of Italy, is a sprawling, cosmopolitan city with nearly 3,000 years of globally influential art, architecture, and culture on display.",
    "image": "https://images.unsplash.com/photo-1598301206763-654c21b40ca3?q=80&w=2070",
    "heroImage": "https://images.unsplash.com/photo-1601924582970-ccf800076915?q=80&w=2070",
    "restaurants": 320,
    "rating": 4.8,
    "tags": ["History", "Ancient Ruins", "Cuisine"],
    "popularCuisines": ["Italian", "Pizza", "Pasta", "Roman Specialties"],
    "bestTimeToVisit": "April to June, September to October",
    "neighborhoods": [
      {
        "name": "Trastevere",
        "description": "Charming cobblestone streets with lively nightlife and authentic Roman trattorias.",
        "image": "https://images.unsplash.com/photo-1592892846440-baf3b217e31a?q=80&w=2070"
      },
      {
        "name": "Monti",
        "description": "Trendy neighborhood with vintage shops and cozy cafés, close to the Colosseum.",
        "image": "https://images.unsplash.com/photo-1585842476250-720ed2be5b7c?q=80&w=2070"
      }
    ]
  }
    };
    
    // Default to Paris if the requested city isn't found
    return cityData[name.toLowerCase()] || cityData.paris;
  };

  // Sample restaurant data
  const restaurants = [
    {
      id: 1,
      name: "Le Petit Bistro",
      cuisine: "French",
      price: "$$$",
      rating: 4.8,
      reviews: 324,
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070",
      neighborhood: "Le Marais",
      featured: true,
      description: "Classic French cuisine in an intimate setting with seasonal ingredients.",
      openHours: "12:00 PM - 10:30 PM"
    },
    {
      id: 2,
      name: "Chez Ahmed",
      cuisine: "Middle Eastern",
      price: "$$",
      rating: 4.7,
      reviews: 187,
      image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2070",
      neighborhood: "Montmartre",
      featured: true,
      description: "Authentic Middle Eastern dishes with halal meat and fresh ingredients.",
      openHours: "11:00 AM - 11:00 PM"
    },
    {
      id: 3,
      name: "Sushi Yoko",
      cuisine: "Japanese",
      price: "$$$$",
      rating: 4.9,
      reviews: 256,
      image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?q=80&w=2070",
      neighborhood: "Saint-Germain-des-Prés",
      featured: false,
      description: "Premium sushi experience with the freshest fish and traditional techniques.",
      openHours: "5:00 PM - 11:00 PM"
    },
    {
      id: 4,
      name: "La Méditerranée",
      cuisine: "Mediterranean",
      price: "$$$",
      rating: 4.5,
      reviews: 142,
      image: "https://images.unsplash.com/photo-1600891964599-f61f4298bdbc?q=80&w=2070",
      neighborhood: "Le Marais",
      featured: false,
      description: "Fresh Mediterranean flavors with a focus on seafood and vegetarian options.",
      openHours: "12:00 PM - 10:00 PM"
    },
    {
      id: 5,
      name: "Pasta Paradiso",
      cuisine: "Italian",
      price: "$$",
      rating: 4.6,
      reviews: 198,
      image: "https://images.unsplash.com/photo-1579684288361-5c1a2957cc38?q=80&w=2071",
      neighborhood: "Montmartre",
      featured: false,
      description: "Handmade pasta and authentic Italian recipes in a cozy atmosphere.",
      openHours: "11:30 AM - 10:30 PM"
    },
    {
      id: 6,
      name: "Le Café Parisien",
      cuisine: "French",
      price: "$$",
      rating: 4.4,
      reviews: 165,
      image: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=2047",
      neighborhood: "Saint-Germain-des-Prés",
      featured: false,
      description: "Classic French café with delicious pastries and light meals.",
      openHours: "7:00 AM - 8:00 PM"
    },
    {
      id: 7,
      name: "Istanbul Delight",
      cuisine: "Turkish",
      price: "$$",
      rating: 4.6,
      reviews: 178,
      image: "https://images.unsplash.com/photo-1603360946369-dc9bb6258143?q=80&w=2835",
      neighborhood: "Le Marais",
      featured: false,
      description: "Authentic Turkish kebabs and mezes with a modern presentation.",
      openHours: "11:00 AM - 11:00 PM"
    },
    {
      id: 8,
      name: "La Belle Époque",
      cuisine: "French",
      price: "$$$$",
      rating: 4.8,
      reviews: 231,
      image: "https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?q=80&w=2070",
      neighborhood: "Saint-Germain-des-Prés",
      featured: true,
      description: "Fine dining French restaurant with an award-winning wine list.",
      openHours: "6:00 PM - 11:00 PM"
    }
  ];

  // Filter restaurants
  const filteredRestaurants = restaurants.filter(restaurant => {
    let match = true;
    
    if (selectedCuisine !== 'All') {
      match = match && restaurant.cuisine === selectedCuisine;
    }
    
    if (selectedPrice !== 'All') {
      match = match && restaurant.price === selectedPrice;
    }
    
    if (selectedRating !== 'All') {
      match = match && restaurant.rating >= parseFloat(selectedRating);
    }
    
    return match;
  });

  // Get unique cuisines, prices and ratings
  const cuisines = ['All', ...new Set(restaurants.map(r => r.cuisine))];
  const prices = ['All', ...new Set(restaurants.map(r => r.price))];
  const ratings = ['All', '4.0', '4.5', '4.8'];

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading city information...</p>
      </div>
    );
  }

  if (!cityData) {
    return (
      <div className="container py-5 text-center">
        <h2>City not found</h2>
        <p>The city you are looking for does not exist or is not available yet.</p>
        <Link to="/cities" className="btn btn-primary mt-3">Back to Cities</Link>
      </div>
    );
  }

  return (
    <div className="city-detail-container">
      {/* Hero Section */}
      <div 
        className="city-hero" 
        style={{ backgroundImage: `url(${cityData.heroImage})` }}
      >
        <div className="container">
          <div className="city-hero-content">
            <h1>{cityData.name}</h1>
            <p className="city-subtitle">{cityData.headline}</p>
            <div className="city-meta">
              <div className="city-meta-item">
                <MapPin size={18} />
                <span>{cityData.country}</span>
              </div>
              <div className="city-meta-item">
                <Star size={18} />
                <span>{cityData.rating} Rating</span>
              </div>
              <div className="city-meta-item">
                <Utensils size={18} />
                <span>{cityData.restaurants} Restaurants</span>
              </div>
            </div>
            <div className="city-tags">
              {cityData.tags.map((tag, index) => (
                <span key={index} className="city-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="city-tabs-container">
        <div className="container">
          <div className="city-tabs">
            <button 
              className={`city-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`city-tab ${activeTab === 'restaurants' ? 'active' : ''}`}
              onClick={() => setActiveTab('restaurants')}
            >
              Restaurants
            </button>
            <button 
              className={`city-tab ${activeTab === 'neighborhoods' ? 'active' : ''}`}
              onClick={() => setActiveTab('neighborhoods')}
            >
              Neighborhoods
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="container py-5">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="city-overview">
            <div className="row">
              <div className="col-lg-8">
                <h2>About {cityData.name}</h2>
                <p className="city-description">{cityData.description}</p>
                
                <div className="city-info-section">
                  <h3>Popular Cuisines</h3>
                  <div className="cuisine-tags">
                    {cityData.popularCuisines.map((cuisine, index) => (
                      <span key={index} className="cuisine-tag">
                        <Coffee size={16} />
                        {cuisine}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="city-info-section">
                  <h3>Featured Restaurants</h3>
                  <div className="row g-4">
                    {restaurants.filter(r => r.featured).slice(0, 3).map(restaurant => (
                      <div key={restaurant.id} className="col-md-6 col-lg-4">
                        <Link to={`/restaurants/${restaurant.id}`} className="text-decoration-none">
                          <div className="featured-restaurant-card">
                            <div className="featured-restaurant-image">
                              <img src={restaurant.image} alt={restaurant.name} />
                              <div className="featured-restaurant-rating">
                                <Star size={14} /> {restaurant.rating}
                              </div>
                            </div>
                            <div className="featured-restaurant-content">
                              <h4>{restaurant.name}</h4>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="cuisine-price">{restaurant.cuisine} • {restaurant.price}</span>
                                <span className="neighborhood">{restaurant.neighborhood}</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="city-info-card">
                  <div className="city-info-item">
                    <Award size={20} className="city-info-icon" />
                    <div>
                      <h4>Best Time to Visit</h4>
                      <p>{cityData.bestTimeToVisit}</p>
                    </div>
                  </div>
                  
                  <div className="city-info-item">
                    <Utensils size={20} className="city-info-icon" />
                    <div>
                      <h4>Top Cuisines</h4>
                      <p>{cityData.popularCuisines.slice(0, 3).join(", ")}</p>
                    </div>
                  </div>
                  
                  <div className="city-info-item">
                    <MapPin size={20} className="city-info-icon" />
                    <div>
                      <h4>Popular Neighborhoods</h4>
                      <p>
                        {cityData.neighborhoods && cityData.neighborhoods.length > 0
                          ? cityData.neighborhoods.map(n => n.name).join(", ")
                          : "Information not available"}
                      </p>
                    </div>
                  </div>
                </div>
                
                <Link to={`/cities/${cityData.name.toLowerCase()}/restaurants`} className="btn btn-primary w-100 mt-4">
                  View All Restaurants
                </Link>
                
                <div className="city-map mt-4">
                  <img 
                    src="https://via.placeholder.com/400x300/f5f5f5/cccccc?text=City+Map" 
                    alt="Map"
                    className="img-fluid rounded"
                  />
                  <p className="text-center mt-2 text-muted small">Interactive map view</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Restaurants Tab */}
        {activeTab === 'restaurants' && (
          <div className="restaurants-tab">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
              <h2>{cityData.name} Restaurants</h2>
              <div className="filter-controls">
                <select 
                  className="form-select me-2" 
                  value={selectedCuisine}
                  onChange={(e) => setSelectedCuisine(e.target.value)}
                >
                  {cuisines.map((cuisine, index) => (
                    <option key={index} value={cuisine}>{cuisine} Cuisine</option>
                  ))}
                </select>
                
                <select 
                  className="form-select me-2" 
                  value={selectedPrice}
                  onChange={(e) => setSelectedPrice(e.target.value)}
                >
                  {prices.map((price, index) => (
                    <option key={index} value={price}>{price === 'All' ? 'Any Price' : price}</option>
                  ))}
                </select>
                
                <select 
                  className="form-select" 
                  value={selectedRating}
                  onChange={(e) => setSelectedRating(e.target.value)}
                >
                  {ratings.map((rating, index) => (
                    <option key={index} value={rating}>
                      {rating === 'All' ? 'Any Rating' : `${rating}+ Stars`}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {filteredRestaurants.length === 0 ? (
              <div className="text-center py-5">
                <p>No restaurants match your selected filters.</p>
                <button 
                  className="btn btn-outline-primary mt-3"
                  onClick={() => {
                    setSelectedCuisine('All');
                    setSelectedPrice('All');
                    setSelectedRating('All');
                  }}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="row g-4">
                {filteredRestaurants.map(restaurant => (
                  <div key={restaurant.id} className="col-md-6 col-lg-4">
                    <div className="restaurant-card">
                      <div className="restaurant-image">
                        <img src={restaurant.image} alt={restaurant.name} />
                        <div className="restaurant-rating">
                          <Star size={16} /> {restaurant.rating}
                        </div>
                      </div>
                      <div className="restaurant-content">
                        <h3 className="restaurant-name">{restaurant.name}</h3>
                        <div className="restaurant-tags">
                          <span>{restaurant.cuisine}</span>
                          <span>{restaurant.price}</span>
                          <span>{restaurant.neighborhood}</span>
                        </div>
                        <p className="restaurant-description">{restaurant.description}</p>
                        <div className="restaurant-footer">
                          <div className="restaurant-hours">
                            <Clock size={16} />
                            <span>{restaurant.openHours}</span>
                          </div>
                          <Link to={`/restaurants/${restaurant.id}`} className="btn btn-sm btn-outline-primary">
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Neighborhoods Tab */}
        {activeTab === 'neighborhoods' && (
          <div className="neighborhoods-tab">
            <h2>{cityData.name} Neighborhoods</h2>
            <p className="mb-4">Explore the diverse culinary scenes across different neighborhoods in {cityData.name}.</p>
            
            {cityData.neighborhoods && cityData.neighborhoods.length > 0 ? (
              <div className="row g-4">
                {cityData.neighborhoods.map((neighborhood, index) => (
                  <div key={index} className="col-md-6 col-lg-4">
                    <div className="neighborhood-card">
                      <div className="neighborhood-image">
                        <img src={neighborhood.image} alt={neighborhood.name} />
                      </div>
                      <div className="neighborhood-content">
                        <h3>{neighborhood.name}</h3>
                        <p>{neighborhood.description}</p>
                        <div className="neighborhood-restaurant-count">
                          {restaurants.filter(r => r.neighborhood === neighborhood.name).length} Restaurants
                        </div>
                        <button className="btn btn-outline-primary btn-sm w-100 mt-2">
                          Explore {neighborhood.name}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-info">
                Neighborhood information is not available for {cityData.name} yet.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Similar Cities Section */}
      <div className="similar-cities-section py-5">
        <div className="container">
          <h2 className="mb-4">Similar Cities You Might Like</h2>
          <div className="row g-4">
            {/* This would typically use real similar cities data */}
            <div className="col-6 col-md-3">
              <Link to="/city/rome" className="similar-city-card">
                <img src="https://images.unsplash.com/photo-1552832230-c0197dd311b5?q=80&w=2096" alt="Rome" />
                <div className="similar-city-overlay">
                  <h3>Rome</h3>
                  <span>Italy</span>
                </div>
              </Link>
            </div>
            <div className="col-6 col-md-3">
              <Link to="/city/istanbul" className="similar-city-card">
                <img src="https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?q=80&w=2071" alt="Istanbul" />
                <div className="similar-city-overlay">
                  <h3>Istanbul</h3>
                  <span>Turkey</span>
                </div>
              </Link>
            </div>
            <div className="col-6 col-md-3">
              <Link to="/city/barcelona" className="similar-city-card">
                <img src="https://images.unsplash.com/photo-1539037116277-4db20889f2d4?q=80&w=2070" alt="Barcelona" />
                <div className="similar-city-overlay">
                  <h3>Barcelona</h3>
                  <span>Spain</span>
                </div>
              </Link>
            </div>
            <div className="col-6 col-md-3">
              <Link to="/city/amsterdam" className="similar-city-card">
                <img src="https://images.unsplash.com/photo-1534351590666-13e3e96b5017?q=80&w=2070" alt="Amsterdam" />
                <div className="similar-city-overlay">
                  <h3>Amsterdam</h3>
                  <span>Netherlands</span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityDetail;